import type { SmartLinkWidgetType } from 'fym-common/src/db/enums';
import type { StudioFormat, StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import type { SoundCloudConnectResult } from '../../modules/music24/soundcloud/soundCloudConnectResult';

export enum SMLRouteNames {
  home = 'home',
  welcome = 'welcome',
  addSong = 'addSong',
  watchlist = 'watchlist',
  trends = 'trends',
  markets = 'markets',
  artists = 'artists',
  artistDetails = 'artistDetails',
  tracks = 'tracks',
  trackDetails = 'trackDetails',
  playlists = 'playlists',
  playlistDetails = 'playlistDetails',
  browse = 'browse',
  /** Your intelligence → Streaming (it replaces Insights). Sections are sub-paths: `/streaming/tracks`. */
  streaming = 'streaming',
  /** Your intelligence → Social (it replaces Promote → Performance). `/social/post/:id` opens a post. */
  social = 'social',
  /** Your intelligence → Fans: the fans who get every new release (Pre-save Subscriptions). */
  fans = 'fans',
  /** The plans page: Free, the paid plans and the compare table. Every "See plans" button opens it. */
  plans = 'plans',
  /** "Review MUSIC24": the review form of the signed-in user (music24reviews.com). */
  review = 'review',
  smartlinks = 'smartlinks',
  catalog = 'catalog',
  catalogLyrics = 'catalogLyrics',
  auth = 'auth',
  account = 'account',
  create = 'create',
  edit = 'edit',
  duplicate = 'duplicate',
  details = 'details',
  manage = 'manage',
  widgetEdit = 'widgetEdit',
  releaseLink = 'releaseLink',
  playlistLink = 'playlistLink',
  presaveLink = 'presaveLink',
  trackers = 'trackers',
  trackerEdit = 'trackerEdit',
  // ---------------------------- Promote ----------------------------
  promote = 'promote',
  promoteCalendar = 'promoteCalendar',
  promoteChannels = 'promoteChannels',
  promoteLibrary = 'promoteLibrary',
  promoteRelease = 'promoteRelease',
  promoteStudio = 'promoteStudio',
  /** Studio → Profile: the pictures and the words of the artist (spec 2026-10-17 artist Profile). */
  promoteProfile = 'promoteProfile',
  promoteWork = 'promoteWork',
  promoteAssets = 'promoteAssets',
  promotePosts = 'promotePosts',
  promoteComposer = 'promoteComposer',
  // ---------------------------- Outreach ----------------------------
  /**
   * Outreach → Pitch (spec 2026-10-11 §6). The sub-pages are plain path segments
   * (`/outreach/find`, `/outreach/campaigns/<uuid>/review`): `smlRoutePath` writes them and
   * `extractLocationFromPath` reads them back.
   */
  outreach = 'outreach',
  outreachProfile = 'outreachProfile',
  outreachFind = 'outreachFind',
  /** Outreach → Community spots: the read-only "Where you can post yourself" list (design r1). */
  outreachCommunities = 'outreachCommunities',
  outreachContacts = 'outreachContacts',
  outreachInbox = 'outreachInbox',
  outreachCampaignNew = 'outreachCampaignNew',
  /** `/outreach/campaigns/<uuid>`: open it with `outreachCampaignPath`. */
  outreachCampaign = 'outreachCampaign',
  /** `/outreach/campaigns/<uuid>/review`: open it with `outreachReviewPath`. */
  outreachReview = 'outreachReview',
  /** `/outreach/results`: the results of every song side by side (design outreach-v2 n2). */
  outreachResults = 'outreachResults',
  // ---------------------------- Profile ----------------------------
  settings = 'settings',
  debug = 'debug',
  // ---------------------------- Payment ----------------------------
  paymentComplete = 'paymentComplete',
  // ---------------------------- Public share ----------------------------
  public = 'public',
  /**
   * The share link of one made asset: `/app/a/<token>`. A member lands on the asset in its release;
   * anybody else sees the public view. The short name keeps the old `link.music24.com/a/<token>` path.
   */
  a = 'a',
}

export type SMLRouteParts = SMLRouteNames[];

type SMLRoutesParts = { [key in keyof typeof SMLRouteNames]: SMLRouteParts };

export const SMLRoutes: SMLRoutesParts = {
  [SMLRouteNames.home]: [SMLRouteNames.home],
  [SMLRouteNames.welcome]: [SMLRouteNames.welcome],
  [SMLRouteNames.addSong]: [SMLRouteNames.addSong],
  [SMLRouteNames.watchlist]: [SMLRouteNames.watchlist],
  [SMLRouteNames.trends]: [SMLRouteNames.trends],
  [SMLRouteNames.markets]: [SMLRouteNames.markets],
  [SMLRouteNames.artists]: [SMLRouteNames.artists],
  [SMLRouteNames.artistDetails]: [SMLRouteNames.artists, SMLRouteNames.artistDetails],
  [SMLRouteNames.tracks]: [SMLRouteNames.tracks],
  [SMLRouteNames.trackDetails]: [SMLRouteNames.tracks, SMLRouteNames.trackDetails],
  [SMLRouteNames.playlists]: [SMLRouteNames.playlists],
  [SMLRouteNames.playlistDetails]: [SMLRouteNames.playlists, SMLRouteNames.playlistDetails],
  [SMLRouteNames.browse]: [SMLRouteNames.browse],
  [SMLRouteNames.streaming]: [SMLRouteNames.streaming],
  [SMLRouteNames.social]: [SMLRouteNames.social],
  [SMLRouteNames.fans]: [SMLRouteNames.fans],
  [SMLRouteNames.plans]: [SMLRouteNames.plans],
  [SMLRouteNames.review]: [SMLRouteNames.review],
  [SMLRouteNames.settings]: [SMLRouteNames.settings],
  [SMLRouteNames.debug]: [SMLRouteNames.debug],
  [SMLRouteNames.smartlinks]: [SMLRouteNames.smartlinks],
  [SMLRouteNames.catalog]: [SMLRouteNames.catalog],
  // The lyric sync editor of one song as its own page, so the native apps can open it in their
  // web view (the Catalog page there hands a song tap back to the native sheet).
  [SMLRouteNames.catalogLyrics]: [SMLRouteNames.catalog, SMLRouteNames.catalogLyrics],
  [SMLRouteNames.auth]: [SMLRouteNames.auth],
  [SMLRouteNames.create]: [SMLRouteNames.smartlinks, SMLRouteNames.create],
  [SMLRouteNames.edit]: [SMLRouteNames.details, SMLRouteNames.edit],
  [SMLRouteNames.duplicate]: [SMLRouteNames.manage, SMLRouteNames.duplicate],
  [SMLRouteNames.details]: [SMLRouteNames.details],
  [SMLRouteNames.manage]: [SMLRouteNames.manage],
  [SMLRouteNames.widgetEdit]: [SMLRouteNames.details, SMLRouteNames.widgetEdit],
  [SMLRouteNames.releaseLink]: [SMLRouteNames.smartlinks, SMLRouteNames.releaseLink],
  [SMLRouteNames.playlistLink]: [SMLRouteNames.smartlinks, SMLRouteNames.playlistLink],
  [SMLRouteNames.presaveLink]: [SMLRouteNames.smartlinks, SMLRouteNames.presaveLink],
  [SMLRouteNames.trackers]: [SMLRouteNames.trackers],
  [SMLRouteNames.trackerEdit]: [SMLRouteNames.trackers, SMLRouteNames.trackerEdit],
  // The Promote pillar. `promote` is the Releases list; every other screen is a real route, so
  // each one is deep-linkable and shareable.
  [SMLRouteNames.promote]: [SMLRouteNames.promote],
  [SMLRouteNames.promoteCalendar]: [SMLRouteNames.promote, SMLRouteNames.promoteCalendar],
  [SMLRouteNames.promoteChannels]: [SMLRouteNames.promote, SMLRouteNames.promoteChannels],
  [SMLRouteNames.promoteLibrary]: [SMLRouteNames.promote, SMLRouteNames.promoteLibrary],
  [SMLRouteNames.promoteRelease]: [SMLRouteNames.promote, SMLRouteNames.promoteRelease],
  [SMLRouteNames.promoteStudio]: [SMLRouteNames.promote, SMLRouteNames.promoteStudio],
  [SMLRouteNames.promoteProfile]: [SMLRouteNames.promote, SMLRouteNames.promoteProfile],
  [SMLRouteNames.promoteWork]: [SMLRouteNames.promote, SMLRouteNames.promoteWork],
  [SMLRouteNames.promoteAssets]: [SMLRouteNames.promote, SMLRouteNames.promoteAssets],
  [SMLRouteNames.promotePosts]: [SMLRouteNames.promote, SMLRouteNames.promotePosts],
  [SMLRouteNames.promoteComposer]: [SMLRouteNames.promote, SMLRouteNames.promoteComposer],
  [SMLRouteNames.outreach]: [SMLRouteNames.outreach],
  [SMLRouteNames.outreachProfile]: [SMLRouteNames.outreach, SMLRouteNames.outreachProfile],
  [SMLRouteNames.outreachFind]: [SMLRouteNames.outreach, SMLRouteNames.outreachFind],
  [SMLRouteNames.outreachCommunities]: [SMLRouteNames.outreach, SMLRouteNames.outreachCommunities],
  [SMLRouteNames.outreachContacts]: [SMLRouteNames.outreach, SMLRouteNames.outreachContacts],
  [SMLRouteNames.outreachInbox]: [SMLRouteNames.outreach, SMLRouteNames.outreachInbox],
  [SMLRouteNames.outreachCampaignNew]: [SMLRouteNames.outreach, SMLRouteNames.outreachCampaignNew],
  [SMLRouteNames.outreachCampaign]: [SMLRouteNames.outreach, SMLRouteNames.outreachCampaign],
  [SMLRouteNames.outreachReview]: [SMLRouteNames.outreach, SMLRouteNames.outreachReview],
  [SMLRouteNames.outreachResults]: [SMLRouteNames.outreach, SMLRouteNames.outreachResults],
  [SMLRouteNames.account]: [SMLRouteNames.account],
  [SMLRouteNames.paymentComplete]: [SMLRouteNames.paymentComplete],
  [SMLRouteNames.public]: [SMLRouteNames.public],
  [SMLRouteNames.a]: [SMLRouteNames.a],
};

export type SMLRootParamList = {
  [SMLRouteNames.home]: undefined;
  [SMLRouteNames.welcome]: undefined;
  [SMLRouteNames.addSong]: undefined;
  [SMLRouteNames.watchlist]: undefined;
  [SMLRouteNames.trends]: undefined;
  [SMLRouteNames.markets]: undefined;
  [SMLRouteNames.artists]: undefined;
  [SMLRouteNames.artistDetails]: { artistId: string };
  [SMLRouteNames.tracks]: undefined;
  [SMLRouteNames.trackDetails]: { trackId: string };
  [SMLRouteNames.playlists]: undefined;
  [SMLRouteNames.playlistDetails]: { playlistId: string };
  [SMLRouteNames.browse]: undefined;
  [SMLRouteNames.streaming]: undefined;
  [SMLRouteNames.social]: undefined;
  [SMLRouteNames.fans]: undefined;
  /**
   * `plan` + `interval`: the plan a website "Choose <plan>" button named (ruling C2). `from`: the
   * page that opened the plans, for Back.
   */
  [SMLRouteNames.plans]: { plan?: string; interval?: string; from?: string } | undefined;
  [SMLRouteNames.review]: undefined;
  /** The tab (`live`, `draft`; none = All) and the search of the SmartLinks page. */
  [SMLRouteNames.smartlinks]: { tab?: 'live' | 'draft'; search?: string } | undefined;
  [SMLRouteNames.catalog]: undefined;
  /**
   * The lyric sync editor of one catalog row (design release-kit s8, s9). The native apps add
   * `accountId`, which the web switcher reads (`resolveActiveAccountId.ts`).
   */
  [SMLRouteNames.catalogLyrics]: { catalogItemUuid: string; accountId?: number };
  // `mode=signin` opens the sign-in copy (design onboarding g10).
  [SMLRouteNames.auth]: { email?: string; returnTo?: string; mode?: 'signin' } | undefined;
  [SMLRouteNames.account]: undefined;
  [SMLRouteNames.create]: { accountId: number };
  [SMLRouteNames.edit]: { accountId: number; smartLinkId?: number };
  [SMLRouteNames.duplicate]: { accountId: number; smartLinkId: number };
  /**
   * The SmartLink editor. Its view state is in the URL (design smartlink-editor a1, b1): `tab` is the
   * panel tab, `block` the open block row, `add=1` the block picker and `after` the block above the
   * new one.
   */
  [SMLRouteNames.details]: {
    accountId: number;
    smartLinkId: number;
    tab?: 'blocks' | 'look' | 'page';
    block?: string;
    add?: '1';
    after?: string;
  };
  [SMLRouteNames.manage]: { accountId: number; smartLinkId: number };
  [SMLRouteNames.widgetEdit]: {
    accountId: number;
    smartLinkId: number;
    widgetType: SmartLinkWidgetType;
    widgetUUID?: string;
  };
  /** `catalogTrackMaid` opens the wizard straight on that release. */
  [SMLRouteNames.releaseLink]: { accountId: number; catalogTrackMaid?: string };
  [SMLRouteNames.playlistLink]: { accountId: number };
  /** `catalogItemUuid` opens the wizard on a form already filled from that row. */
  [SMLRouteNames.presaveLink]: { accountId: number; catalogItemUuid?: string };
  [SMLRouteNames.trackers]: undefined;
  [SMLRouteNames.trackerEdit]: { accountId: number; trackerId?: number };
  // ---------------------------- Promote ----------------------------
  /** The Releases list (spec D25). */
  [SMLRouteNames.promote]: undefined;
  /**
   * The release page of one catalog row (design assets-rethink b1): Overview (default), Kit (the
   * keepers), Posts (the post files of the song) or Plan. The old `tab=assets` opens Kit, or Posts
   * for a post file in `asset` or with `focus=aiReel`.
   */
  [SMLRouteNames.promoteRelease]: {
    catalogItemUuid: string;
    /** The tab; no tab opens Overview. `assets` is the old name (a redirect by the kind). */
    tab?: 'overview' | 'kit' | 'posts' | 'plan' | 'campaign' | 'assets';
    focus?: 'aiReel';
    /** The asset to open in the viewer (Kit or Posts tab): a kit asset or a reel uuid. */
    asset?: string;
    /** The revision of `asset` to open (design d1). */
    rev?: string;
    /** The artist to select first (an asset share link of another artist). */
    accountId?: number;
  };
  /**
   * The Calendar. `view`, `date` (`YYYY-MM-DD`) and the filters (`songs`, `channels`, `states`,
   * comma lists) keep the view. `post` (or the old `postId` of the push and email links) opens the
   * day panel of that post.
   */
  [SMLRouteNames.promoteCalendar]:
    | {
        view?: 'month' | 'week' | 'list';
        date?: string;
        songs?: string;
        channels?: string;
        states?: string;
        post?: string;
        postId?: string;
      }
    | undefined;
  /** `soundCloud`: how a SoundCloud sign-in ended; Channels shows it in a banner. */
  [SMLRouteNames.promoteChannels]: { soundCloud?: SoundCloudConnectResult } | undefined;
  [SMLRouteNames.promoteLibrary]: undefined;
  /**
   * The Studio (spec D28). `catalogItemUuid` opens it on that song, `format` and `theme` on that
   * step. `reel` opens that AI reel (theme `aiReel`).
   */
  [SMLRouteNames.promoteStudio]:
    | {
        catalogItemUuid?: string;
        format?: StudioFormat;
        theme?: StudioThemeId;
        reel?: string;
        /** Opens the result (design n5) of this kit asset of the song: "Open" on the Work page. */
        result?: string;
      }
    | undefined;
  /**
   * Studio → Profile (spec 2026-10-17 artist Profile, design artist-profile a–d). `tool` opens one
   * tool (`profile/profileRoute.ts`), `banner` picks the tab of the banners tool, `sheet=spotify`
   * opens the Upload to Spotify sheet. Back closes the tool or the sheet.
   */
  [SMLRouteNames.promoteProfile]:
    | {
        tool?: 'spotifyHeader' | 'spotifyAvatar' | 'banners' | 'pressPhoto' | 'bio';
        banner?: 'youtubeBanner' | 'facebookCover';
        sheet?: 'spotify' | 'spotifyBio' | 'sendAll';
      }
    | undefined;
  /**
   * The Work page (owner, 2026-10-11: a menu item and a page again): the asset generations of the
   * artist that are queued, made now or failed, and the files that finished a short time ago. The
   * plain route and `state=working|failed` show the page (`catalogItemUuid` keeps one song,
   * `result=` / `reel=` marks one row). A link of a finished file still opens the file in its home
   * (`promote/work/workLinkTarget.ts`): `result=` / `reel=`, or `catalogItemUuid` with
   * `kind=keeper|post`. The native apps open their own Work screen from the same links.
   */
  [SMLRouteNames.promoteWork]:
    | {
        catalogItemUuid?: string;
        state?: 'working' | 'ready' | 'failed';
        result?: string;
        reel?: string;
        kind?: 'keeper' | 'post';
      }
    | undefined;
  /**
   * The old Assets page. It redirects by the kind of the file: `asset=` opens the file in its home (the
   * Kit tab for a keeper, Posts for a post), `state=` + `catalogItemUuid` the Kit tab or Posts of the
   * song, every other link opens Posts.
   */
  [SMLRouteNames.promoteAssets]:
    | {
        asset?: string;
        state?: 'ready' | 'queued' | 'working' | 'failed';
        catalogItemUuid?: string;
      }
    | undefined;
  /**
   * The Posts page (design assets-rethink c1, c2): the post files of the artist in the lanes Ready
   * to post, Scheduled and Posted. `catalogItemUuid` keeps one song, `platform` one channel,
   * `asset` opens a file in the viewer, `view=archive` shows the archive (`outcome` posted | never).
   */
  [SMLRouteNames.promotePosts]:
    | {
        catalogItemUuid?: string;
        platform?: string;
        asset?: string;
        view?: 'archive';
        outcome?: 'posted' | 'never';
      }
    | undefined;
  /**
   * `postUuid` opens the composer on an existing post to edit it. `platform` preselects one
   * channel, and `date` (`YYYY-MM-DD`) with `time` (`HH:mm`) sets the time: this is how a day or
   * a free slot of the Calendar hands over.
   */
  [SMLRouteNames.promoteComposer]: { postUuid?: string; platform?: string; date?: string; time?: string } | undefined;
  // ---------------------------- Outreach ----------------------------
  [SMLRouteNames.outreach]: undefined;
  [SMLRouteNames.outreachProfile]: undefined;
  /**
   * `kind` opens a tab, `song` (a catalog item uuid) the release the fit is for, `campaign` the draft
   * that "Pitch these" adds the picked targets to.
   */
  [SMLRouteNames.outreachFind]: { kind?: string; song?: string; campaign?: string } | undefined;
  /** `song` (a catalog item uuid) rides the route; the list fits the sound profile, not one song. */
  [SMLRouteNames.outreachCommunities]: { song?: string } | undefined;
  [SMLRouteNames.outreachContacts]: { list?: string; status?: string; tag?: string; q?: string } | undefined;
  [SMLRouteNames.outreachInbox]: { label?: string; campaign?: string } | undefined;
  /**
   * The campaign builder (design outreach e). `song` picks the release, `campaign` opens a saved
   * draft, `targets` (comma list of target uuids) and `list` fill step 2 Who.
   */
  [SMLRouteNames.outreachCampaignNew]:
    | { song?: string; campaign?: string; targets?: string; list?: string }
    | undefined;
  [SMLRouteNames.outreachCampaign]: undefined;
  [SMLRouteNames.outreachReview]: undefined;
  [SMLRouteNames.outreachResults]: undefined;
  // ---------------------------- Profile ----------------------------
  /** `addOnPosts` / `addOnCredits` preset the add-on sliders of the Subscription tab (design limit-upsell a1). */
  [SMLRouteNames.settings]: { tab?: string; addOnPosts?: string; addOnCredits?: string } | undefined;
  [SMLRouteNames.debug]: undefined;
  [SMLRouteNames.paymentComplete]: undefined;
  [SMLRouteNames.public]: { code: string };
  [SMLRouteNames.a]: { token: string };
};
